/**
 * Stage 1 — risk assessment → risk items → GATE 1 (confirm).
 *
 * The items are extracted from the attached release risk assessment. The lead
 * may re-rate, exclude, or annotate each one; an override keeps the original
 * rating beside it so the audit trail and the plan both see the disagreement.
 */

import { useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconCheck, IconDeviceFloppy, IconInfoCircle, IconLink } from "@tabler/icons-react";
import { Link as RouterLink } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { confirmRisks, updateRisks, type RiskItem, type RunOut } from "@/shared/api/validation";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { fmtDate, riskColor } from "../stageMeta";
import { AdvisoryNote, Provenance } from "./Provenance";
import { Stage1Start } from "./Stage1Start";

const RATINGS = ["High", "Medium", "Low", "N/A"];

/** Human labels for the ITQRA buckets — mirrors validation_itqra.LABELS. */
const ITQRA_LABEL: Record<string, Record<string, string>> = {
  process_type: {
    calculation_validation: "Calculation / Validation",
    change_of_data: "Change of data",
    display: "Display",
  },
  business_impact: {
    compliance: "Compliance",
    wrong_information: "Wrong information",
    negligible: "Negligible",
  },
  use_frequency: { very_often: "Very often", often: "Often", rare: "Rare" },
};

/** NN's ITQRA scoring, shown as the computed basis for the level: the three
 *  weighted dimensions and the A/B/C score. */
function ItqraBreakdown({ item }: { item: RiskItem }) {
  const c = item.itqra_computed;
  const s = item.itqra;
  if (!c) return null;
  const cell = (
    dim: "process_type" | "business_impact" | "use_frequency",
    rationale?: string | null,
  ) => (
    <Tooltip label={rationale || ""} disabled={!rationale} multiline w={260}>
      <Text span size="xs">
        {ITQRA_LABEL[dim]?.[c[dim]] ?? c[dim]} <b>({c.weights[dim]})</b>
      </Text>
    </Tooltip>
  );
  return (
    <Group gap={6} mt={4} wrap="wrap">
      <Badge size="xs" variant="outline" color="grape">
        ITQRA {c.band} · {c.score}/16
      </Badge>
      {cell("process_type", s?.process_rationale)}
      <Text span size="xs" c="dimmed">
        ·
      </Text>
      {cell("business_impact", s?.impact_rationale)}
      <Text span size="xs" c="dimmed">
        ·
      </Text>
      {cell("use_frequency", s?.frequency_rationale)}
    </Group>
  );
}

interface Props {
  run: RunOut;
  onUpdated: (run: RunOut, jump?: boolean) => void;
}

export function RiskStage({ run, onUpdated }: Props) {
  const getToken = useAccessToken();
  const editable = run.status === "assessed" && run.is_owner;
  const [items, setItems] = useState<RiskItem[]>(run.risks ?? []);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<"save" | "confirm" | "attach" | null>(null);

  useEffect(() => {
    setItems(run.risks ?? []);
    setDirty(false);
  }, [run.risks]);

  const edit = (id: string, patch: Partial<RiskItem>) => {
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const save = async (): Promise<boolean> => {
    setBusy("save");
    try {
      onUpdated(await updateRisks(await getToken(), run.run_id, items), false);
      toastSuccess("Risk items saved", "Your edits are recorded.");
      return true;
    } catch (err) {
      toastApiError("Could not save risk items", err);
      return false;
    } finally {
      setBusy(null);
    }
  };

  const confirm = async () => {
    if (dirty && !(await save())) return;
    setBusy("confirm");
    try {
      const fresh = await confirmRisks(await getToken(), run.run_id);
      onUpdated(fresh);
      toastSuccess("Risks confirmed", "The plan and the test scripts have been drafted.");
    } catch (err) {
      toastApiError("Could not confirm risks", err);
    } finally {
      setBusy(null);
    }
  };

  if (!run.risk_assessment_id) {
    // End to end: run the assessment here, or attach one that already exists.
    return <Stage1Start run={run} onUpdated={onUpdated} />;
  }

  const confirmedCount = items.filter((r) => r.confirmed).length;
  const overrides = items.filter(
    (r) => r.original_gxp_risk && r.original_gxp_risk !== r.gxp_risk,
  ).length;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="xs">
            <Text fw={600}>Risk items from the release assessment</Text>
            <Anchor component={RouterLink} to="/ai/risk-assessment" size="xs">
              <Group gap={4}>
                <IconLink size={12} /> open the assessment
              </Group>
            </Anchor>
          </Group>
          <Provenance
            promptVersion={run.risks_prompt_version}
            inferenceId={run.risks_inference_id}
          />
          {run.risks_confirmed_at_utc ? (
            <Text size="xs" c="dimmed">
              Confirmed {fmtDate(run.risks_confirmed_at_utc)} · {confirmedCount} in scope ·{" "}
              {overrides} override{overrides === 1 ? "" : "s"}
            </Text>
          ) : (
            <AdvisoryNote />
          )}
        </Stack>
        {editable && (
          <Group gap="xs">
            <Button
              variant="light"
              leftSection={<IconDeviceFloppy size={16} />}
              disabled={!dirty}
              loading={busy === "save"}
              onClick={() => void save()}
            >
              Save edits
            </Button>
            <Tooltip label="Human gate 1 — freezes the risks and drafts the validation plan">
              <Button
                leftSection={<IconCheck size={16} />}
                loading={busy === "confirm"}
                disabled={confirmedCount === 0}
                onClick={() => void confirm()}
              >
                Confirm {confirmedCount} risk{confirmedCount === 1 ? "" : "s"} & draft plan
              </Button>
            </Tooltip>
          </Group>
        )}
      </Group>

      {busy === "confirm" && (
        <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />}>
          Drafting the Release Validation Plan and the test scripts from the confirmed risks — two
          model calls, this can take a minute or two.
        </Alert>
      )}

      <Paper withBorder radius="md" p={0}>
        <Table verticalSpacing="sm" highlightOnHover={!editable}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={70}>Id</Table.Th>
              <Table.Th>Finding</Table.Th>
              <Table.Th w={130}>GxP risk</Table.Th>
              <Table.Th w={130}>Category</Table.Th>
              <Table.Th w={90}>In scope</Table.Th>
              {editable && <Table.Th w={240}>Override note</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((r) => {
              const overridden = r.original_gxp_risk && r.original_gxp_risk !== r.gxp_risk;
              return (
                <Table.Tr key={r.id} style={{ opacity: r.confirmed ? 1 : 0.5 }}>
                  <Table.Td>
                    <Badge variant="outline" color="gray" size="sm">
                      {r.id}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {r.title}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {r.area}
                      {r.affected_objects.length > 0 ? ` · ${r.affected_objects.join(", ")}` : ""}
                    </Text>
                    {r.rationale && (
                      <Text size="xs" mt={2}>
                        {r.rationale}
                      </Text>
                    )}
                    {r.source && (
                      <Text size="xs" c="dimmed" fs="italic">
                        {r.source}
                      </Text>
                    )}
                    {r.itqra_computed && <ItqraBreakdown item={r} />}
                    {!editable && r.override_note && (
                      <Text size="xs" c="orange.7" mt={2}>
                        Override: {r.override_note}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {editable ? (
                      <Select
                        size="xs"
                        data={RATINGS}
                        value={r.gxp_risk}
                        onChange={(v) => v && edit(r.id, { gxp_risk: v })}
                        allowDeselect={false}
                      />
                    ) : (
                      <Badge size="sm" variant="light" color={riskColor(r.gxp_risk)}>
                        {r.gxp_risk}
                      </Badge>
                    )}
                    {overridden && (
                      <Text size="xs" c="dimmed" mt={2}>
                        was {r.original_gxp_risk}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{r.category ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    {editable ? (
                      <Switch
                        size="sm"
                        checked={r.confirmed}
                        onChange={(e) => edit(r.id, { confirmed: e.currentTarget.checked })}
                      />
                    ) : (
                      <Badge size="sm" variant="light" color={r.confirmed ? "green" : "gray"}>
                        {r.confirmed ? "yes" : "excluded"}
                      </Badge>
                    )}
                  </Table.Td>
                  {editable && (
                    <Table.Td>
                      <TextInput
                        size="xs"
                        placeholder={overridden || !r.confirmed ? "why?" : ""}
                        value={r.override_note ?? ""}
                        onChange={(e) => edit(r.id, { override_note: e.currentTarget.value })}
                      />
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>
    </Stack>
  );
}
